import { Injectable, computed, inject, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { DateAggregation, ChartDto, DatePeriod } from 'src/app/shared/models/chart.model';
import { UseOfGoodsGroupType } from '../enums/use-of-goods-group-type.enum';
import { UseOfGoodsService } from '../services/use-of-goods.service';
import { TimeSeriesResponseDto } from 'src/app/shared/models/time-series-response.model';
import { endOfDay, endOfMonth, endOfWeekSunday, endOfYear, startOfDay, startOfMonth, startOfWeekMonday, startOfYear } from 'src/app/shared/utils/date.utils';
import { LoadingService, OVERLAY_POSITION } from 'src/app/shared/services/loading.service';


export interface UseOfGoodsState {
  range: DateAggregation;
  period: DatePeriod;
  groupType: UseOfGoodsGroupType;
  dateFrom?: Date;
  dateTo?: Date;
  data: TimeSeriesResponseDto | null;
}

@Injectable({ providedIn: 'root' })
export class UseOfGoodsStore {
  loadingService = inject(LoadingService);

  private readonly _state = signal<UseOfGoodsState>({
    range: DateAggregation.Year,
    period: DatePeriod.ThirteenMonths,
    data: null,
    groupType: UseOfGoodsGroupType.ByRecipes
  });

  readonly state = this._state.asReadonly();

  readonly range = computed(() => this.state().range);
  readonly period = computed(() => this.state().period);
  readonly data = computed(() => this.state().data);
  readonly groupType = computed(() => this.state().groupType);

  constructor(private readonly service: UseOfGoodsService) {}

  setPeriod(period: DatePeriod) {
    this._state.update((s) => ({ ...s, period }));
  }
  setGroupType(groupType: UseOfGoodsGroupType) {
    this._state.update((s) => ({ ...s, groupType }));
  }

  load(period: DatePeriod, groupType: UseOfGoodsGroupType) {
     this.loadingService.show(OVERLAY_POSITION.COMPONENT)
     
    let dateFrom = new Date();
    let dateTo = new Date();
    const now = new Date();
    switch (period) {
      case DatePeriod.FourteenDays: {
        // 14 Tage inkl. heute => heute + 13 Tage zurück
        dateTo = endOfDay(now);
        dateFrom = startOfDay(now);
        dateFrom.setDate(dateFrom.getDate() - 13);
        this._state.update((s) => ({ ...s, dateFrom, groupType,dateTo, range: DateAggregation.Day }));
        break;
      }

      case DatePeriod.TenWeeks: {
        // 10 Wochen inkl. aktueller Woche
        dateFrom = startOfWeekMonday(now);
        dateFrom.setDate(dateFrom.getDate() - 9 * 7); // 9 Wochen zurück + aktuelle = 10
        dateTo = endOfWeekSunday(now);
        this._state.update((s) => ({ ...s, dateFrom, groupType, dateTo, range: DateAggregation.Week }));
        break;
      }

      case DatePeriod.ThirteenMonths: {
        // 13 Monate inkl. aktueller Monat
        dateFrom = startOfMonth(now);
        dateFrom.setMonth(dateFrom.getMonth() - 12); // 12 Monate zurück + aktueller = 13
        dateTo = endOfMonth(now);
        this._state.update((s) => ({ ...s, dateFrom, groupType, dateTo, range: DateAggregation.Month }));
        break;
      }

      case DatePeriod.EightQuarter: {
        // 8 Quartale = 4 Vorjahr + 4 aktuelles Jahr
        const y = now.getFullYear();
        dateFrom = startOfYear(y - 1);
        dateTo = endOfYear(y);
        this._state.update((s) => ({ ...s, dateFrom, dateTo, groupType, range: DateAggregation.Quarter }));
        break;
      }

      case DatePeriod.AllYears: {
        // hier vorerst mal 12 Jahre zurück, damit es nicht zu unübersichtlich wird
        const y = now.getFullYear();
         dateFrom = startOfYear(y - 12);
        dateTo = endOfYear(y);
        this._state.update((s) => ({ ...s, dateFrom, dateTo, groupType, range: DateAggregation.Year }));
        break;
      }

      default: {
        dateFrom = startOfDay(now);
        dateTo = endOfDay(now);
      }
    }

    this.service
      .getUseOfGoods({ dateFrom: this.state().dateFrom ?? new Date(), dateTo: this.state().dateTo ?? new Date(), aggregateBy: this.state().range, useOfGoodsGroupType: this.state().groupType })
      .pipe(finalize(() => this.loadingService.hide(OVERLAY_POSITION.COMPONENT)))
      .subscribe({
        next: (data) => this._state.update((s) => ({ ...s, data })),
        error: (err) => {
          this.loadingService.hide(OVERLAY_POSITION.COMPONENT);
          throw err;          
        }
      });
  }
}
